import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, AlertTriangle, ChevronDown, Check } from "lucide-react";
import { api, clearJsonLd, setJsonLd, setSeo, track } from "@/lib/api";
import { CommercialModule } from "@/components/site/CommercialModule";
import { ToolRelated } from "@/components/site/RelatedLinks";

export function useTool(slug) {
  const [tool, setTool] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/tools/${slug}`).then((r) => {
      const t = r.data;
      setTool(t);
      setSeo({ title: `${t.name} — Human Home Living`, description: t.summary });
      const origin = window.location.origin;
      const url = `${origin}/tools/${slug}`;
      // A genuinely free, browser-based planning tool, so WebApplication is accurate here.
      setJsonLd("ld-tool", {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: t.name,
        url,
        description: t.content?.intro || t.summary,
        applicationCategory: "UtilitiesApplication",
        operatingSystem: "Any",
        browserRequirements: "Requires JavaScript",
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        publisher: { "@type": "Organization", name: "Human Home Living", url: origin },
      });
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "Planning tools", item: `${origin}/tools` },
          { "@type": "ListItem", position: 3, name: t.name },
        ],
      });
      if (t.content?.faq?.length) {
        setJsonLd("ld-faq", {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: t.content.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        });
      } else {
        clearJsonLd("ld-faq");
      }
    });
    track({ event_type: "page_view", page: `/tools/${slug}`, tool_slug: slug });
  }, [slug]);
  return tool;
}

export const ToolHeader = ({ tool }) => (
  <header className="bg-charcoal text-white px-5 md:px-8 pt-12 pb-14">
    <div className="max-w-[900px] mx-auto">
      <nav className="text-[12.5px] text-white/75 mb-6">
        <Link to="/" className="hover:text-white">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/tools" className="hover:text-white">
          Planning tools
        </Link>
      </nav>
      <span className="eyebrow eyebrow-light">Planning tool · {tool?.barrier}</span>
      <h1 className="display text-[36px] md:text-[52px] leading-[1.05] mt-4">{tool?.name}</h1>
      <p className="text-[17px] md:text-[19px] leading-[1.55] text-[#CBC8C2] mt-5 max-w-2xl">
        {tool?.summary}
      </p>
    </div>
  </header>
);

export const Progress = ({ step, total }) => (
  <div className="flex items-center gap-3 mb-10" data-testid="tool-progress">
    <div className="flex-1 h-[2px] bg-line relative">
      <div
        className="absolute inset-y-0 left-0 bg-sage transition-all duration-500"
        style={{ width: `${((step + 1) / total) * 100}%` }}
      />
    </div>
    <span className="text-[12px] tracking-[0.14em] uppercase text-[#4F4B45] whitespace-nowrap">
      Step {step + 1} / {total}
    </span>
  </div>
);

export const Question = ({ label, hint, children }) => (
  <div className="mb-9">
    <h3 className="text-[19px] md:text-[22px] font-medium text-charcoal leading-snug">{label}</h3>
    {hint && <p className="text-[16px] md:text-[16.5px] text-[#4F4B45] mt-2.5 leading-relaxed">{hint}</p>}
    <div className="mt-5">{children}</div>
  </div>
);

export const Choices = ({ options, value, onChange, multi = false, testId, cols = 2 }) => {
  const isOn = (v) => (multi ? (value || []).includes(v) : value === v);
  const pick = (v) => {
    if (!multi) return onChange(v);
    const cur = value || [];
    onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  return (
    <div className={`grid gap-2.5 ${cols === 1 ? "" : "sm:grid-cols-2"}`} data-testid={testId}>
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const note = typeof o === "string" ? null : o.note;
        return (
          <button
            key={val}
            type="button"
            onClick={() => pick(val)}
            data-testid={`${testId}-${String(val).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`text-left px-5 py-4 min-h-[56px] border transition-all duration-300 ${
              isOn(val)
                ? "border-sage bg-sage-soft"
                : "border-line bg-white hover:border-[#BFC7BA]"
            }`}
          >
            <span className="text-[15.5px] text-charcoal">{label}</span>
            {note && <span className="block text-[14px] text-[#4F4B45] mt-1">{note}</span>}
          </button>
        );
      })}
    </div>
  );
};

// Physical-dimension validation. Rejects blank, non-numeric, zero, negative and values outside a
// plausible residential range so a result can never be generated from an impossible measurement.
export function dimensionError(value, { min = 1, max = 100, unit = "ft" } = {}) {
  if (value === null || value === undefined || value === "") return "Enter a measurement to continue.";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a number.";
  if (n <= 0) return "Must be greater than zero.";
  if (n < min) return `That looks too small — check the measurement (minimum ${min} ${unit}).`;
  if (n > max) return `That looks too large — check the measurement (maximum ${max} ${unit}).`;
  return null;
}

export const NumberField = ({
  label, unit, value, onChange, testId, placeholder, error, showError = false,
}) => {
  const [touched, setTouched] = useState(false);
  const visible = error && (touched || showError);
  return (
    <label className="block">
      <span className="text-[14.5px] text-[#454545]">{label}</span>
      <div
        className={`flex items-center border bg-white mt-2 transition-colors ${
          visible ? "border-[#B4471F]" : "border-line focus-within:border-sage"
        }`}
      >
        <input
          type="number"
          inputMode="decimal"
          value={value ?? ""}
          placeholder={placeholder}
          onBlur={() => setTouched(true)}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={testId}
          aria-invalid={visible ? "true" : "false"}
          className="w-full px-4 py-3.5 text-[16px] bg-transparent focus:outline-none"
        />
        {unit && <span className="px-4 text-[14px] text-[#4F4B45]">{unit}</span>}
      </div>
      {visible && (
        <span
          data-testid={testId ? `${testId}-error` : undefined}
          className="block text-[12.5px] text-[#B4471F] mt-1.5"
        >
          {error}
        </span>
      )}
    </label>
  );
};

export const Nav = ({ step, total, onBack, onNext, nextLabel, disabled }) => (
  <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-line">
    <button
      type="button"
      onClick={onBack}
      disabled={step === 0}
      data-testid="tool-back"
      className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-[#7A766F] hover:text-charcoal disabled:opacity-35 transition-colors"
    >
      <ArrowLeft size={15} /> Back
    </button>
    <button
      type="button"
      onClick={onNext}
      disabled={disabled}
      data-testid="tool-next"
      className="inline-flex items-center gap-2 px-8 py-4 bg-charcoal text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors duration-300 disabled:opacity-40 disabled:hover:bg-charcoal"
    >
      {nextLabel || (step === total - 1 ? "See my result" : "Continue")} <ArrowRight size={15} />
    </button>
  </div>
);

export const Panel = ({ children }) => (
  <div className="bg-white border border-line px-6 md:px-10 py-9 md:py-11">{children}</div>
);

export const ResultBlock = ({ label, value, emphasis = false, testId }) => (
  <div className={`py-5 border-b border-line ${emphasis ? "" : ""}`} data-testid={testId}>
    <div className="text-[11px] tracking-[0.16em] uppercase text-[#4F4B45] font-medium">{label}</div>
    <div
      className={
        emphasis
          ? "display text-[27px] md:text-[32px] leading-tight text-charcoal mt-2"
          : "text-[16.5px] md:text-[17px] leading-[1.6] text-[#3A3A3A] mt-2"
      }
    >
      {value}
    </div>
  </div>
);

export const ResultList = ({ label, items, testId }) => (
  <div className="py-5 border-b border-line" data-testid={testId}>
    <div className="text-[11px] tracking-[0.16em] uppercase text-[#4F4B45] font-medium mb-3">{label}</div>
    <ul className="space-y-2.5">
      {items.map((i, k) => (
        <li key={k} className="text-[16.5px] md:text-[17px] leading-[1.6] text-[#3A3A3A] flex gap-3">
          <span className="text-sage-dark mt-0.5">—</span>
          <span>{i}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Secondary/technical detail, collapsed so the homeowner sees the answer before the report.
export const Details = ({ label, children, testId }) => (
  <details className="group border-b border-line" data-testid={testId}>
    <summary className="flex items-center justify-between gap-4 py-5 cursor-pointer list-none">
      <span className="text-[12.5px] tracking-[0.12em] uppercase text-charcoal font-semibold">{label}</span>
      <ChevronDown size={17} className="text-[#8A867F] transition-transform duration-300 group-open:rotate-180 shrink-0" />
    </summary>
    <div className="pb-5 -mt-1">{children}</div>
  </details>
);

// The single scannable answer at the top of every result.
export const ResultHeadline = ({ eyebrow, headline, body, reasons, testId }) => (
  <div data-testid={testId}>
    <span className="eyebrow">{eyebrow}</span>
    <h2 className="display text-[30px] md:text-[40px] leading-[1.12] text-charcoal mt-4">{headline}</h2>
    {body && <p className="text-[17px] md:text-[18px] leading-[1.7] text-[#3A3A3A] mt-5">{body}</p>}
    {reasons?.length ? (
      <ul className="mt-6 space-y-2.5" data-testid={testId ? `${testId}-reasons` : undefined}>
        {reasons.map((r, k) => (
          <li key={k} className="text-[16.5px] md:text-[17px] leading-[1.6] text-[#3A3A3A] flex gap-3">
            <span className="text-sage-dark mt-0.5">—</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    ) : null}
  </div>
);

export const Disclaimer = ({ children }) => (
  <div className="flex gap-3.5 bg-bone border border-line px-5 py-4 mt-8" data-testid="tool-disclaimer">
    <AlertTriangle size={17} className="text-[#B4741F] shrink-0 mt-0.5" />
    <p className="text-[15px] leading-relaxed text-[#454545]">{children}</p>
  </div>
);

// The actionable payoff of every tool: what works for this visitor, directly under the short
// result and ahead of the deeper explanation. Options are derived from each tool's existing
// result logic — no product, merchant, price or affiliate link is invented here.
export const WorkableOptions = ({
  eyebrow = "What works for you",
  headline,
  options = [],
  productPath,
  tool,
  testId = "workable-options",
}) => (
  <section
    data-testid={testId}
    className="mt-10 border border-line border-t-[3px] border-t-charcoal bg-bone px-6 md:px-9 py-8 md:py-10"
  >
    <span className="text-[11.5px] tracking-[0.18em] uppercase text-[#48563F] font-semibold">
      {eyebrow}
    </span>
    <h3 className="display text-[27px] md:text-[34px] leading-[1.15] text-charcoal mt-3">
      {headline}
    </h3>

    {options.length > 0 && (
      <ul className="mt-7 space-y-2.5" data-testid={`${testId}-list`}>
        {options.map((o, k) => (
          <li
            key={k}
            data-testid={`${testId}-item-${k}`}
            className="flex gap-3.5 bg-white border border-line border-l-[3px] border-l-sage px-4 md:px-5 py-4"
          >
            <Check size={17} className="text-sage-dark shrink-0 mt-[3px]" />
            <span className="text-[16.5px] md:text-[17px] leading-[1.55] text-charcoal">{o}</span>
          </li>
        ))}
      </ul>
    )}

    <div
      className="mt-8 bg-white border-2 border-[#C3CCBB] px-5 md:px-7 py-7 md:py-8"
      data-testid={`${testId}-product-path`}
    >
      <div className="text-[15px] md:text-[16px] tracking-[0.13em] uppercase text-[#48563F] font-bold">
        Matched product options
      </div>
      {productPath && (
        <p className="text-[18px] md:text-[19.5px] leading-[1.5] text-charcoal font-medium mt-3 max-w-2xl">
          {productPath}
        </p>
      )}
      <p className="text-[14.5px] leading-relaxed text-[#54504A] mt-3.5 max-w-2xl">
        This is where options matched to the answers you gave will appear. Affiliate relationships do
        not determine whether a product is appropriate for your home.
      </p>
      <div className="mt-5">
        <CommercialModule
          category={tool?.offer_category}
          label={tool?.cta_label || "See Current Options"}
          placement="TOOL RESULT CTA"
          toolSlug={tool?.slug}
          compact
          tone="strong"
          size="lg"
        />
      </div>
    </div>
  </section>
);

export const ResultFooter = ({ onReset }) => (
  <div className="mt-10 pt-8 border-t border-line">
    <button
      type="button"
      onClick={onReset}
      data-testid="tool-reset"
      className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-[#7A766F] hover:text-charcoal transition-colors"
    >
      <RotateCcw size={14} /> Start again
    </button>
  </div>
);

// Supporting content for a tool page: what to measure before starting, how the result is
// calculated, the limits of the output, and the guides that continue the same decision. Content
// comes from the tool record (backend/tool_content.py) so nothing is hard-coded here.
const SupportList = ({ block, testId }) =>
  block?.items?.length ? (
    <section className="mt-12 pt-10 border-t border-line" data-testid={testId}>
      <h2 className="display text-[24px] md:text-[28px] leading-tight text-charcoal">
        {block.label}
      </h2>
      <ul className="mt-5 space-y-3">
        {block.items.map((i, k) => (
          <li key={k} className="text-[16.5px] md:text-[17px] leading-[1.65] text-[#3A3A3A] flex gap-3">
            <span className="text-sage-dark mt-0.5">—</span>
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </section>
  ) : null;

export const ToolSupport = ({ content, slug }) => {
  const [openFaq, setOpenFaq] = useState(null);
  if (!content) return null;
  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-8" data-testid={`tool-support-${slug}`}>
      {content.intro && (
        <p
          data-testid="tool-intro"
          className="text-[17px] md:text-[18.5px] leading-[1.7] text-[#3A3A3A] mt-12 max-w-3xl"
        >
          {content.intro}
        </p>
      )}
      <SupportList block={content.measure} testId="tool-measure" />
      <SupportList block={content.how} testId="tool-how" />
      <SupportList block={content.limits} testId="tool-limits" />

      {content.faq?.length > 0 && (
        <section className="mt-12 pt-10 border-t border-line" data-testid="tool-faq">
          <h2 className="display text-[24px] md:text-[28px] leading-tight text-charcoal">
            Common questions
          </h2>
          <div className="mt-4 border-t border-line">
            {content.faq.map((f, k) => (
              <div key={k} className="border-b border-line">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === k ? null : k)}
                  data-testid={`tool-faq-toggle-${k}`}
                  className="w-full flex items-start justify-between gap-5 py-5 text-left group"
                >
                  <span className="text-[16.5px] md:text-[17.5px] font-medium text-charcoal group-hover:text-sage-dark transition-colors">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={19}
                    className={`text-[#736F68] shrink-0 mt-0.5 transition-transform duration-300 ${
                      openFaq === k ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* Kept in the page source when collapsed, so the answer stays indexable. */}
                <p
                  data-testid={`tool-faq-answer-${k}`}
                  hidden={openFaq !== k}
                  className="text-[16px] leading-[1.7] text-[#4A4A4A] pb-6 pr-8"
                >
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {content.links?.items?.length > 0 && (
        <section className="mt-12 pt-10 border-t border-line" data-testid="tool-context-links">
          <h2 className="display text-[24px] md:text-[28px] leading-tight text-charcoal">
            {content.links.label}
          </h2>
          <ul className="mt-5 space-y-5">
            {content.links.items.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`tool-context-link-${l.to.split("/").pop()}`}
                  className="text-[17px] md:text-[18px] leading-snug text-[#48563F] font-medium underline decoration-sage/50 underline-offset-4 hover:text-sage-dark transition-colors"
                >
                  {l.label}
                </Link>
                {l.note && (
                  <p className="text-[15.5px] leading-relaxed text-[#4F4B45] mt-1.5">{l.note}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export const ToolFrame = ({ tool, children }) => (
  <div data-testid={`tool-page-${tool?.slug}`}>
    <ToolHeader tool={tool} />
    <div className="px-5 md:px-8 -mt-8 pb-8">
      <div className="max-w-[900px] mx-auto">
        {children}
      </div>
    </div>
    <ToolSupport content={tool?.content} slug={tool?.slug} />
    <div className="pb-20">
      <ToolRelated slug={tool?.slug} />
    </div>
  </div>
);

export function useToolEvents(slug) {
  const started = useRef(false);
  const start = () => {
    if (!started.current) {
      started.current = true;
      track({ event_type: "tool_start", tool_slug: slug });
    }
  };
  const complete = (meta) => track({ event_type: "tool_complete", tool_slug: slug, meta: meta || {} });
  const resetRun = () => {
    started.current = false;
  };
  return { start, complete, resetRun };
}
